import { Box, Flex, LinkBox, LinkOverlay, Stack, Tag, Text } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { displayDate } from "../../common/formatting/displayDate";
import { displaySum } from "../../common/formatting/displaySum";
import { EventStatusTag } from "../../common/sourcing-events/EventStatusTag";
import { NeedStatusTag } from "../behov/NeedStatusTag";
import { monthName, plannedStartOf, type PlanEntry } from "./procurementPlanEntries";

export const PlanEntryCard = ({ entry, variant = "timeline" }: { entry: PlanEntry; variant?: "timeline" | "board" }) => {
  const { t, i18n } = useTranslation();
  return (
    <LinkBox
      as="article"
      bg="smBackground"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="md"
      p="3"
      minW="0"
      _hover={{ bg: "smPrimarySofter" }}
      data-testid={variant === "board" ? "plan-card" : "plan-entry"}
    >
      <Stack spacing="2" minW="0">
        <Flex gap="2" align="center" wrap="wrap">
          {entry.kind === "need" && (
            <Tag size="sm" colorScheme="gray" variant="subtle">
              {t("Need")}
            </Tag>
          )}
          {entry.status?.kind === "procurement" && (
            <EventStatusTag event={{ kind: "PUBLIC", status: entry.status.status }} size="sm" />
          )}
          {entry.status?.kind === "need" && <NeedStatusTag status={entry.status.status} />}
          {variant === "board" && entry.negotiating && (
            <Tag size="sm" colorScheme="purple" variant="subtle">
              {t("Negotiation")}
            </Tag>
          )}
        </Flex>
        <LinkOverlay
          as={RouterLink}
          to={entry.link}
          fontWeight="semibold"
          color="smText"
          noOfLines={variant === "board" ? 2 : undefined}
          sx={{ hyphens: "auto", overflowWrap: "anywhere" }}
        >
          {entry.title}
        </LinkOverlay>
        {variant === "board" ? (
          <BoardDetails entry={entry} language={i18n.language} />
        ) : (
          <TimelineDetails entry={entry} language={i18n.language} />
        )}
      </Stack>
    </LinkBox>
  );
};

const TimelineDetails = ({ entry, language }: { entry: PlanEntry; language: string }) => {
  const { t } = useTranslation();
  const plannedStart = plannedStartOf(entry);
  return (
    <Box as="dl" fontSize="sm" display="grid" gridTemplateColumns="auto 1fr" columnGap="3" rowGap="0.5">
      <Field label={t("Regulation")} value={entry.regulation} />
      <Field label={t("Procedure")} value={entry.procedure} />
      <Field label={t("Responsible")} value={entry.responsible?.name ?? null} />
      <Field label={t("Department")} value={entry.department?.name ?? null} />
      <Field label={planValueLabel(t, entry)} value={planValueText(t, entry)} />
      {plannedStart ? (
        <Field label={t("Planned start")} value={monthName(plannedStart, language)} />
      ) : (
        <Field label={t("Tender deadline")} value={entry.deadline ? displayDate(entry.deadline) : null} />
      )}
      <Field label={t("Desired delivery")} value={entry.desiredDelivery ? displayDate(entry.desiredDelivery) : null} />
    </Box>
  );
};

const BoardDetails = ({ entry, language }: { entry: PlanEntry; language: string }) => {
  const { t } = useTranslation();
  return (
    <Stack spacing="0.5" fontSize="sm" minW="0">
      <Text color="smText" fontWeight="medium">
        {planValueText(t, entry) ?? `${planValueLabel(t, entry)} –`}
      </Text>
      {entry.kind === "need" && entry.valueSource && planValueText(t, entry) && (
        <Text color="smMuted" fontSize="xs">
          {planValueLabel(t, entry)}
        </Text>
      )}
      <Text color="smMuted">{boardDate(t, entry, language)}</Text>
      {entry.responsible && (
        <Text color="smMuted" noOfLines={1} wordBreak="break-all">
          {entry.responsible.name}
        </Text>
      )}
      {entry.department && (
        <Text color="smMuted" noOfLines={1} wordBreak="break-all">
          {entry.department.name}
        </Text>
      )}
    </Stack>
  );
};

const Field = ({ label, value }: { label: string; value: string | null }) => (
  <>
    <Text as="dt" color="smMuted">
      {label}
    </Text>
    <Text as="dd" color="smText" noOfLines={1}>
      {value || "–"}
    </Text>
  </>
);

export const planValueLabel = (t: TFunction, entry: PlanEntry): string =>
  entry.valueSource === "procurer" ? t("Procurer's value") : t("Estimated value");

const planValueText = (t: TFunction, entry: PlanEntry): string | null =>
  entry.value !== null
    ? displaySum({ value: entry.value, currency: entry.currency })
    : entry.valueUnknown
      ? t("Don't know")
      : null;

const boardDate = (t: TFunction, entry: PlanEntry, language: string): string => {
  if (entry.kind === "procurement") {
    return entry.deadline ? t("Deadline {{date}}", { date: displayDate(entry.deadline) }) : t("No date given");
  }
  const plannedStart = plannedStartOf(entry);
  if (plannedStart) return t("Starts {{month}}", { month: monthName(plannedStart, language) });
  if (entry.desiredDelivery) return t("Delivery {{date}}", { date: displayDate(entry.desiredDelivery) });
  return t("No date given");
};
